import { useMemo } from 'react'

import { toast } from 'react-toastify'
import { useSwitchChain } from 'wagmi'

import * as Sentry from '@sentry/react'

import { ChainId } from '@src/configs/chainInfo'
import { LiquiditySource, ResultCode, StepNames } from '@src/enums'
import { useExchangeTokenEvm } from '@src/hooks/useExchangeToken/useExchangeTokenEvm'
import { useExchangeTokenSolana } from '@src/hooks/useExchangeToken/useExchangeTokenSolana'
import { useExchangeTokenTron } from '@src/hooks/useExchangeToken/useExchangeTokenTron'
import { useWalletConnections } from '@src/hooks/useWalletConnections'
import { useExchangeDataContext } from '@src/pages/Exchange/context/ExchangeDataContext'
import { useSwapContract } from '@src/pages/Exchange/hooks/useSwapContract'
import { ChainHandler, IExchangeData, UpdateData } from '@src/types/exchange'
import { IQuoteData } from '@src/types/order'
import { getChecksumAddress } from '@src/utils/address'
import { isFeatureEnabled } from '@src/utils/environment'
import { isUnWrapOnly, isWrapOnly } from '@src/utils/exchangeUtils'
import { getBlockExplorerUrlFromChainId } from '@src/utils/getBlockExplorerUrl'
import { createContextLogger } from '@src/utils/logger'
import { mapTokenAddress } from '@src/utils/tokenAddressMapping'
import { useApplicationStore } from '@src/zustand_stores/application.store'
import { DEFAULT_STEPS, useHistoryStore } from '@src/zustand_stores/history.store'
import { useInvalidateBalances } from '@src/zustand_stores/tokens.store'

interface ExchangeTokenReturn {
    exchange: (referrer?: string, quote?: IQuoteData) => Promise<void>
}

const logger = createContextLogger('useExchangeToken')

export const useExchangeToken = (
    exchangeId: string,
    liquiditySource: LiquiditySource,
    destinationAddress?: string,
    // @ts-ignore @typescript-eslint/no-empty-function
    onSwapSuccess: () => void = () => {}
): ExchangeTokenReturn => {
    // Zustand hooks
    const setWaitingForConnectedWalletInteraction = useApplicationStore(
        state => state.setWaitingForConnectedWalletInteraction
    )
    const getOrder = useHistoryStore(state => state.getOrder)

    const updateHistory = useHistoryStore(state => state.updateHistory)
    const { currentStep } = getOrder(exchangeId)

    // Add the invalidateBalances function
    const invalidateBalances = useInvalidateBalances()

    /**
     * * * * * * * * * * * * * * * * * * * * * NEW EXCHANGE DATA CONTEXT * * * * * * * * * * * * * * *
     */
    const { sellData, buyData } = useExchangeDataContext()
    /**
     * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * * *
     */

    // Chain switching
    const { switchChainAsync } = useSwitchChain()

    // Wallet connections
    const { tronWalletAddress, solanaWalletAddress, EVMChainId, evmWalletAddress } = useWalletConnections()

    // Contract setup
    const swapContract = useSwapContract(sellData, liquiditySource)

    // Initialize chain handlers
    const evmHandler = useExchangeTokenEvm(swapContract)
    const tronHandler = useExchangeTokenTron(swapContract)
    const solanaHandler = useExchangeTokenSolana()

    // Get appropriate chain handler based on chainId
    const getChainHandler = useMemo(() => {
        const handlers = {
            [ChainId.Solana]: solanaHandler,
            [ChainId.Tron]: tronHandler,
        } as Record<number, ChainHandler>
        return (chainId: number) => handlers[chainId] || evmHandler
    }, [solanaHandler, tronHandler, evmHandler])

    // Handle network switching for EVM chains
    const handleNetworkSwitch = async (targetChainId: number) => {
        if (targetChainId !== ChainId.Solana && targetChainId !== ChainId.Tron && targetChainId !== EVMChainId) {
            const updateData = {
                result: {
                    status: ResultCode.pending,
                    message: 'Time to switch network.',
                    transactionETA: 0,
                },
            }

            updateHistory(exchangeId, updateData)
            await switchChainAsync({ chainId: targetChainId })
        }
    }

    // Get wallet address based on chain
    const getWalletAddress = (chainId: number): string => {
        switch (chainId) {
            case ChainId.Solana:
                return solanaWalletAddress || ''
            case ChainId.Tron:
                return tronWalletAddress || ''
            default:
                return evmWalletAddress || ''
        }
    }

    const validateInputs = (sellData: IExchangeData, buyData: IExchangeData, quote?: IQuoteData) => {
        logger.debug('Validating inputs:', {
            sellToken: {
                address: sellData.token?.address,
                symbol: sellData.token?.symbol,
            },
            buyToken: {
                address: buyData.token?.address,
                symbol: buyData.token?.symbol,
            },
            quote: quote
                ? {
                      srcAsset: quote.src_asset_address,
                      dstAsset: quote.dst_asset_address,
                  }
                : 'No quote',
        })

        if (!sellData.value || !sellData.token || !buyData.token) {
            logger.error('Missing required data:', {
                'sellData.value': sellData.value,
                'sellData.token': sellData.token,
                'buyData.token': buyData.token,
            })
            throw Error('validateInputs error: Invalid data')
        }

        if (quote) {
            // Map both quote and token addresses
            const mappedSellTokenAddress = mapTokenAddress(sellData.token.address, true, sellData.chainId)
            const mappedQuoteSrcAddress = mapTokenAddress(quote.src_asset_address, true, sellData.chainId)
            const mappedBuyTokenAddress = mapTokenAddress(buyData.token.address, true, buyData.chainId)
            const mappedQuoteDstAddress = mapTokenAddress(quote.dst_asset_address, true, buyData.chainId)

            // Convert addresses to checksum format for comparison
            const sellTokenMismatch =
                getChecksumAddress(mappedQuoteSrcAddress) !== getChecksumAddress(mappedSellTokenAddress)
            const buyTokenMismatch =
                getChecksumAddress(mappedQuoteDstAddress) !== getChecksumAddress(mappedBuyTokenAddress)

            logger.debug('Token address comparison:', {
                sell: {
                    quoteAddress: getChecksumAddress(mappedQuoteSrcAddress),
                    tokenAddress: getChecksumAddress(mappedSellTokenAddress),
                    matches: !sellTokenMismatch,
                },
                buy: {
                    quoteAddress: getChecksumAddress(mappedQuoteDstAddress),
                    tokenAddress: getChecksumAddress(mappedBuyTokenAddress),
                    matches: !buyTokenMismatch,
                },
            })

            if (sellTokenMismatch || buyTokenMismatch) {
                logger.debug('Token address mismatch:', {
                    sell: {
                        expected: getChecksumAddress(mappedSellTokenAddress),
                        got: getChecksumAddress(mappedQuoteSrcAddress),
                        symbol: sellData.token.symbol,
                    },
                    buy: {
                        expected: getChecksumAddress(mappedBuyTokenAddress),
                        got: getChecksumAddress(mappedQuoteDstAddress),
                        symbol: buyData.token.symbol,
                    },
                })
                throw Error('validateInputs error: Invalid token address')
            }
        }

        if (liquiditySource === LiquiditySource.unavailable) {
            toast.error('Error: No liquidity available or quote expired.', {
                position: 'bottom-right',
                autoClose: 5000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            })
            return
        }
    }

    const isRateLimited = (error: any): boolean => {
        try {
            console.log('Checking rate limit on error:', error)

            // Direct check for our specific rate limit error format from useExchangeTokenEvm
            if (error instanceof Error && error.message.startsWith('Rate limited. Please try again in')) {
                return true
            }

            // Original error has message "Cannot send order data" but comes from a 429
            if (error instanceof Error && error.message === 'Cannot send order data') {
                // Since we know from the logs this is often a 429 error in testnet
                return true
            }

            // Direct status check
            if (error?.status === 429) return true

            // Response status check (axios style)
            if (error?.response?.status === 429) return true

            // Message content check - make sure message exists and is a string
            const message = typeof error?.message === 'string' ? error.message.toLowerCase() : ''
            if (
                message &&
                (message.includes('429') || message.includes('rate limit') || message.includes('too many requests'))
            )
                return true

            // Check detail message which might contain rate limit info
            const detailMessage =
                typeof error?.response?.data?.detail === 'string'
                    ? error.response.data.detail.toLowerCase()
                    : typeof error?.data?.detail === 'string'
                      ? error.data.detail.toLowerCase()
                      : ''

            if (detailMessage && (detailMessage.includes('too many requests') || detailMessage.includes('rate limit')))
                return true

            // Sometimes the error might be wrapped in other objects
            if (error?.data?.status === 429) return true

            return false
        } catch (e) {
            console.error('Error checking rate limit:', e)
            // If anything fails, assume it's not rate limited
            return false
        }
    }

    const getWaitTime = (error: any): number => {
        try {
            // Default wait time
            const defaultTime = 30

            // Check our custom error format first
            if (
                error instanceof Error &&
                error.message &&
                error.message.startsWith('Rate limited. Please try again in')
            ) {
                const match = error.message.match(/Rate limited\. Please try again in (\d+) seconds\./)
                if (match && match[1]) {
                    const seconds = parseInt(match[1])
                    return isNaN(seconds) ? defaultTime : seconds
                }
            }

            // Safely extract detail message
            const detail = error?.data?.detail || error?.response?.data?.detail

            if (!detail || typeof detail !== 'string') {
                return defaultTime
            }

            // Updated regex to match actual backend format: "Too many requests. Please wait X.X seconds."
            const waitMatch = detail.match(/Too many requests\. Please wait (\d+\.?\d*) seconds\./)
            if (waitMatch && waitMatch[1]) {
                const parsedTime = parseFloat(waitMatch[1])
                return isNaN(parsedTime) ? defaultTime : Math.ceil(parsedTime)
            }

            return defaultTime
        } catch {
            // If anything fails, return default wait time
            return 30
        }
    }

    const handleRateLimitedError = (waitTime: number) => {
        console.log('handleRateLimitedError called with waitTime:', waitTime)
        try {
            // Ensure waitTime is valid
            const safeWaitTime = waitTime && waitTime > 0 ? waitTime : 30
            toast.error(`Rate limited. Please try again in ${safeWaitTime} seconds.`)
        } catch {
            // Absolute fallback
            toast.error('Rate limited. Please try again later.')
        }
    }

    const exchange = async (referrer?: string, quote?: IQuoteData): Promise<void> => {
        logger.info('exchange called:', { sellData, buyData, quote })

        logger.info(
            `currentStep=${currentStep} isWrapOnly=${isWrapOnly(sellData, buyData)} isUnWrapOnly=${isUnWrapOnly(sellData, buyData)}`
        )

        try {
            const updateData: UpdateData = {
                exchangeId,
                steps: DEFAULT_STEPS,
                result: {
                    status: ResultCode.pending,
                    message: 'Processing. Please be patient.',
                    transactionETA: 0,
                },
            }

            // Handle network switching if needed
            await handleNetworkSwitch(sellData.chainId)

            // Validate inputs unless it's a wrap/unwrap operation
            if (!isWrapOnly(sellData, buyData) && !isUnWrapOnly(sellData, buyData)) {
                validateInputs(sellData, buyData, quote)
            }

            const chainHandler = getChainHandler(sellData.chainId)
            const walletAddress = getWalletAddress(sellData.chainId)

            if (!walletAddress) {
                throw new Error('No wallet connected')
            }

            logger.info(`[exchange] currentStep=${currentStep} walletAddress=${walletAddress}`)

            if ((currentStep === StepNames.DEPOSIT_WETH || isWrapOnly(sellData, buyData)) && chainHandler.handleWrap) {
                const hash = await chainHandler.handleWrap(sellData.value, updateData, sellData)
                logger.info(`[exchange] Wrap tx hash=${hash}`)

                if (isWrapOnly(sellData, buyData)) {
                    updateHistory(null, {
                        ...updateData,
                        currentStep: StepNames.MATCH_EXECUTED,
                        result: {
                            ...updateData.result,
                            status: ResultCode.success,
                            sourceTxHash: `${getBlockExplorerUrlFromChainId(sellData.chainId)}/${hash}`,
                        },
                    })

                    onSwapSuccess()

                    // Invalidate balances to refresh after wrap
                    invalidateBalances()

                    setWaitingForConnectedWalletInteraction(false)
                    return
                }
            }

            if (
                (currentStep === StepNames.UNWRAP_WETH || isUnWrapOnly(sellData, buyData)) &&
                chainHandler.handleUnwrap
            ) {
                const hash = await chainHandler.handleUnwrap(sellData.value, updateData, sellData)

                if (isUnWrapOnly(sellData, buyData)) {
                    updateHistory(null, {
                        ...updateData,
                        currentStep: StepNames.MATCH_EXECUTED,
                        result: {
                            ...updateData.result,
                            status: ResultCode.success,
                            sourceTxHash: `${getBlockExplorerUrlFromChainId(sellData.chainId)}/${hash}`,
                        },
                    })

                    onSwapSuccess()

                    // Invalidate balances to refresh after unwrap
                    invalidateBalances()

                    setWaitingForConnectedWalletInteraction(false)
                    return
                }
            }

            try {
                // Handle approval if needed
                if (currentStep === StepNames.APPROVE_TOKENS) {
                    logger.info(`[exchange] Calling handleApproval...`)
                    await chainHandler.handleApproval(updateData, sellData, walletAddress)

                    // Invalidate balances after approval (to update allowances)
                    invalidateBalances()
                }

                // Execute the swap
                const executeSwapData = {
                    sellData,
                    buyData,
                    quote,
                    walletAddress,
                    destinationAddress,
                    liquiditySource,
                    onSuccess: () => {
                        // Call the original onSuccess callback
                        onSwapSuccess()

                        // Invalidate balances after successful swap
                        invalidateBalances()
                    },
                    exchangeId,
                    chainId: sellData.chainId,
                }
                logger.info(`[exchange] approval successful, proceeding to executeSwap with data=`, executeSwapData)
                await chainHandler.executeSwap(executeSwapData, referrer)
            } catch (error) {
                console.log('Original executeSwap error:', error)
                console.log('Error type:', typeof error)
                console.log('Error instanceof Error:', error instanceof Error)
                console.log('Error status:', (error as any)?.status)
                console.log('Error response:', (error as any)?.response)

                // Check if error has axios structure
                if ((error as any)?.response) {
                    console.log('Axios error response:', {
                        status: (error as any).response.status,
                        data: (error as any).response.data,
                    })
                }

                const isUserRejection =
                    (error as Error).message?.includes('rejected') ||
                    (error as Error).message?.includes('user denied') ||
                    (error as Error).message?.includes('user cancelled')

                if (isUserRejection) {
                    updateHistory(exchangeId, {
                        currentStep: currentStep || StepNames.UNSTARTED,
                        result: {
                            status: ResultCode.pending,
                            message: 'Transaction was rejected. Please try again.',
                        },
                    })
                } else {
                    logger.error('Exchange error:', error)
                    Sentry.captureException(error, {
                        tags: {
                            location: 'useExchangeToken:executeSwap',
                            exchangeId,
                            liquiditySource,
                            sellToken: sellData.token?.symbol,
                            buyToken: buyData.token?.symbol,
                            chainId: sellData.chainId,
                        },
                        extra: {
                            sellData,
                            buyData,
                            currentStep,
                        },
                    })

                    if (!isFeatureEnabled('USE_TESTNETS_ONLY')) {
                        toast.error('ERROR executing order placement transaction')
                    } else {
                        console.log('on testnets, checking for rate limiting')
                        // If we're on testnets, we check for rate limiting,
                        // and if that's not the issue, we show a generic error
                        const rateLimited = isRateLimited(error)
                        console.log('rateLimited', rateLimited)
                        // Extract wait time if available
                        const waitTime = getWaitTime(error)
                        console.log('waitTime', waitTime)
                        if (rateLimited) {
                            handleRateLimitedError(waitTime)
                        } else {
                            toast.error('ERROR executing order placement transaction')
                        }
                    }

                    updateHistory(exchangeId, {
                        currentStep: currentStep || StepNames.UNSTARTED,
                        result: {
                            status: ResultCode.failure,
                            message: 'Transaction failed. Please try again.',
                        },
                    })
                }
            }
        } catch (error) {
            logger.error('Exchange error:', error)

            Sentry.captureException(error, {
                tags: {
                    location: 'useExchangeToken:outerTryCatch',
                    exchangeId,
                    liquiditySource,
                },
            })

            updateHistory(exchangeId, {
                currentStep: StepNames.UNSTARTED,
                result: {
                    status: ResultCode.failure,
                    message: 'We encountered an error. Please try again.',
                },
            })
        } finally {
            setWaitingForConnectedWalletInteraction(false)
        }
    }

    return { exchange }
}

export default useExchangeToken
